import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  FileText,
  Plus,
  Edit3,
  Trash2,
  Copy,
  Search,
  LayoutTemplate,
  Variable,
  Clock,
  CheckCircle2,
  Loader2,
  X,
  Save,
  Eye,
  FolderOpen
} from 'lucide-react';

const CustomTemplates = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUseModal, setShowUseModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [variableValues, setVariableValues] = useState({});
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    content: '',
    category: 'Personal'
  });
  const [saving, setSaving] = useState(false);

  const categories = ['Personal', 'Laboral', 'Comercial', 'Arrendamiento', 'Servicios', 'Familiar'];

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/templates/custom');
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Error al cargar plantillas');
    } finally {
      setLoading(false);
    }
  };

  const createTemplate = async () => {
    if (!newTemplate.name || !newTemplate.content) {
      toast.error('Completa el nombre y contenido de la plantilla');
      return;
    }

    setSaving(true);
    try {
      const response = await api.post('/templates/custom', newTemplate);
      toast.success(`Plantilla creada con ${response.data.variables_detected} variables detectadas`);
      setShowCreateModal(false);
      setNewTemplate({ name: '', description: '', content: '', category: 'Personal' });
      fetchTemplates();
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Error al crear plantilla');
    } finally {
      setSaving(false);
    }
  };

  const duplicateTemplate = async (templateId) => {
    try {
      const response = await api.post(`/templates/custom/${templateId}/duplicate`);
      toast.success('Plantilla duplicada');
      fetchTemplates();
    } catch (error) {
      console.error('Error duplicating template:', error);
      toast.error('Error al duplicar plantilla');
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('¿Eliminar esta plantilla?')) return;
    
    try {
      await api.delete(`/templates/custom/${templateId}`);
      toast.success('Plantilla eliminada');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Error al eliminar plantilla');
    }
  };

  const openUseModal = (template) => {
    setSelectedTemplate(template);
    // Initialize variable values
    const initialValues = {};
    (template.variables || []).forEach(v => {
      initialValues[v.name] = v.default_value || '';
    });
    setVariableValues(initialValues);
    setShowUseModal(true);
  };

  const useTemplate = async () => {
    if (!selectedTemplate) return;

    // Check required variables
    const missingRequired = (selectedTemplate.variables || [])
      .filter(v => v.required && !variableValues[v.name]);
    
    if (missingRequired.length > 0) {
      toast.error(`Completa los campos requeridos: ${missingRequired.map(v => v.label).join(', ')}`);
      return;
    }

    setSaving(true);
    try {
      const response = await api.post(`/templates/custom/${selectedTemplate.id}/use`, {
        template_id: selectedTemplate.id,
        variable_values: variableValues
      });
      
      toast.success('Contrato creado desde plantilla');
      setShowUseModal(false);
      navigate(`/contract/${response.data.contract_id}`);
    } catch (error) {
      console.error('Error using template:', error);
      toast.error('Error al crear contrato');
    } finally {
      setSaving(false);
    }
  };

  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
        <span className="ml-2 text-slate-400">Cargando plantillas...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <LayoutTemplate className="w-6 h-6 text-purple-400" />
            Mis Plantillas
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Crea y gestiona tus plantillas personalizadas
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          data-testid="create-template-btn"
          className="btn-primary px-4 py-2 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Nueva Plantilla
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          type="text"
          placeholder="Buscar plantillas..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-purple-500"
          data-testid="search-templates"
        />
      </div>

      {/* Templates Grid */}
      {filteredTemplates.length === 0 ? (
        <div className="text-center py-12 bg-slate-800/50 rounded-xl">
          <FolderOpen className="w-12 h-12 mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400">
            {searchQuery ? 'No se encontraron plantillas' : 'No tienes plantillas personalizadas'}
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 text-purple-400 hover:text-purple-300 text-sm"
          >
            Crear tu primera plantilla
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <div
              key={template.id}
              className="bg-card border border-border rounded-xl overflow-hidden hover:border-purple-500/30 transition-colors"
              data-testid={`template-${template.id}`}
            >
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium truncate">{template.name}</h3>
                    <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">
                      {template.category}
                    </span>
                  </div>
                </div>
                
                {template.description && (
                  <p className="text-sm text-slate-400 line-clamp-2 mb-3">
                    {template.description}
                  </p>
                )}

                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <Variable className="w-3 h-3" />
                    {(template.variables || []).length} variables
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {template.use_count || 0} usos
                  </span>
                </div>
              </div>

              <div className="px-4 py-3 bg-slate-800/50 border-t border-slate-700/50 flex items-center justify-between">
                <button
                  onClick={() => openUseModal(template)}
                  data-testid={`use-template-${template.id}`}
                  className="flex items-center gap-1 text-sm text-purple-400 hover:text-purple-300"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  Usar
                </button>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => duplicateTemplate(template.id)}
                    title="Duplicar"
                    className="p-1.5 text-slate-400 hover:text-blue-400"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteTemplate(template.id)}
                    title="Eliminar"
                    className="p-1.5 text-slate-400 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Template Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <Plus className="w-5 h-5 text-purple-400" />
                Nueva Plantilla
              </h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-1 text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                  placeholder="Ej: Contrato de Arriendo Estándar"
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 focus:outline-none focus:border-purple-500"
                  data-testid="template-name-input"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1">Categoría</label>
                <select
                  value={newTemplate.category}
                  onChange={(e) => setNewTemplate({ ...newTemplate, category: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 focus:outline-none focus:border-purple-500"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1">Descripción</label>
                <input
                  type="text"
                  value={newTemplate.description}
                  onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                  placeholder="Breve descripción de la plantilla"
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 focus:outline-none focus:border-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1">
                  Contenido * 
                  <span className="text-xs text-purple-400 ml-2">
                    Usa {"{{variable}}"} para crear campos dinámicos
                  </span>
                </label>
                <textarea
                  value={newTemplate.content}
                  onChange={(e) => setNewTemplate({ ...newTemplate, content: e.target.value })}
                  placeholder={`Ejemplo:\n\nCONTRATO DE ARRIENDO\n\nEntre {{nombre_arrendador}} (arrendador) y {{nombre_arrendatario}} (arrendatario)...\n\nPropiedad ubicada en: {{direccion_propiedad}}\nCanon mensual: {{valor_arriendo}}`}
                  rows={12}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 focus:outline-none focus:border-purple-500 font-mono text-sm"
                  data-testid="template-content-input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-slate-400 hover:text-white"
                >
                  Cancelar
                </button>
                <button
                  onClick={createTemplate}
                  disabled={saving}
                  data-testid="save-template-btn"
                  className="btn-primary px-4 py-2 flex items-center gap-2"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Guardar Plantilla
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Use Template Modal */}
      {showUseModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-400" />
                Usar Plantilla
              </h3>
              <button
                onClick={() => setShowUseModal(false)}
                className="p-1 text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4">
              <p className="text-sm text-slate-400 mb-4">
                Completa los campos para crear tu contrato desde "{selectedTemplate.name}"
              </p>

              {(selectedTemplate.variables || []).length === 0 ? (
                <p className="text-center py-4 text-slate-500">
                  Esta plantilla no tiene variables
                </p>
              ) : (
                <div className="space-y-3">
                  {(selectedTemplate.variables || []).map((variable) => (
                    <div key={variable.name}>
                      <label className="block text-sm text-slate-400 mb-1">
                        {variable.label}
                        {variable.required && <span className="text-red-400 ml-1">*</span>}
                      </label>
                      {variable.type === 'select' && variable.options ? (
                        <select
                          value={variableValues[variable.name] || ''}
                          onChange={(e) => setVariableValues({
                            ...variableValues,
                            [variable.name]: e.target.value
                          })}
                          className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 focus:outline-none focus:border-purple-500"
                        >
                          <option value="">Seleccionar...</option>
                          {variable.options.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : variable.type === 'date' ? (
                        <input
                          type="date"
                          value={variableValues[variable.name] || ''}
                          onChange={(e) => setVariableValues({
                            ...variableValues,
                            [variable.name]: e.target.value
                          })}
                          className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 focus:outline-none focus:border-purple-500"
                        />
                      ) : (
                        <input
                          type={variable.type === 'number' ? 'number' : 'text'}
                          value={variableValues[variable.name] || ''}
                          onChange={(e) => setVariableValues({
                            ...variableValues,
                            [variable.name]: e.target.value
                          })}
                          placeholder={`Ingresa ${variable.label.toLowerCase()}`}
                          className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 focus:outline-none focus:border-purple-500"
                          data-testid={`var-${variable.name}`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowUseModal(false)}
                  className="px-4 py-2 text-slate-400 hover:text-white"
                >
                  Cancelar
                </button>
                <button
                  onClick={useTemplate}
                  disabled={saving}
                  data-testid="create-from-template-btn"
                  className="btn-primary px-4 py-2 flex items-center gap-2"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4" />
                  )}
                  Crear Contrato
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomTemplates;
